import { z } from 'zod';
import { GameId } from './games.js';
// Symbol set used by all games for now (Neon Candy roster).
// When per-game paytables differentiate symbols (Phase F / M02), each game's
// config will define its own symbol enum. For B2 stub, all 4 games share this set.
export const SymbolKey = z.enum([
    'CHERRY',
    'LEMON',
    'WATERMELON',
    'STRAWBERRY',
    'BLUEBERRY',
    'RAINBOW',
    'CROWN',
    'GOLDEN7',
    'WILD',
    'SCATTER',
    // MULTIPLIER — Sweet Bonanza-style 倍率符号。值由 multiplier_grid 平行数组承载
    // (2 / 3 / 5 / ... / 100)。不参与 cluster；cascade 结束后若 total_win>0
    // 则所有还在网格上的 multiplier 值相加 × total_win。
    'MULTIPLIER',
]);
export const WinLevel = z.enum(['none', 'small', 'big', 'mega', 'super', 'jackpot']);
// `count` is widened: paylines max 5, clusters can hit 30 on 6×5.
// Clusters reuse this shape — `payline_id` carries `cluster_<n>` and
// `positions` enumerates all cells in the cluster blob.
export const WinLine = z.object({
    payline_id: z.string(),
    symbol: SymbolKey,
    count: z.number().int().min(3),
    win_amount: z.number().int().nonnegative(),
    positions: z.array(z.tuple([z.number().int(), z.number().int()])),
});
// One round of a tumble cascade. `clusters` are the wins paid this round;
// `grid_after` is the post-clear post-drop layout the client should render.
export const CascadeStep = z.object({
    round: z.number().int().nonnegative(),
    clusters: z.array(WinLine),
    round_win: z.number().int().nonnegative(),
    grid_after: z.array(z.array(SymbolKey)),
    // Multiplier values parallel to grid_after, used to render multiplier glow this round.
    multiplier_grid_after: z.array(z.array(z.number().int().nonnegative())).optional(),
});
export const Mechanic = z.enum(['paylines', 'cluster_tumble', 'megaways', 'walking_wilds', 'hold_and_win']);
export const SpinRequest = z.object({
    client_request_id: z.string().min(8),
    game_id: GameId,
    room_id: z.string().nullable().default(null),
    bet_amount: z.number().int().min(10_000),
    boost: z.boolean().default(false),
});
export const SpinResponse = z.object({
    spin_id: z.string(),
    game_id: GameId,
    // grid[col][row] — the *initial* board (pre-cascade). For cluster_tumble,
    // each `cascades[].grid_after` carries the next visible board.
    grid: z.array(z.array(SymbolKey)),
    total_win: z.number().int().nonnegative(),
    win_lines: z.array(WinLine),
    scatter_count: z.number().int().nonnegative(),
    bonus_triggered: z.boolean(),
    bonus_session_id: z.string().nullable(),
    free_spins_awarded: z.number().int().nonnegative().nullable(),
    wallet_balance_after: z.number().int().nonnegative(),
    display_hints: z.object({
        win_level: WinLevel,
        animation_key: z.string(),
    }),
    // Optional, mechanic-specific. Absence = paylines mechanic / no cascades.
    mechanic: Mechanic.optional(),
    cascades: z.array(CascadeStep).optional(),
    // Multiplier symbol grid (parallel to grid)：
    //   multiplier_grid[col][row] = 0 means no multiplier; >0 means multiplier value at that cell.
    //   仅 cluster_tumble 用。
    multiplier_grid: z.array(z.array(z.number().int().nonnegative())).optional(),
    // Sum of multiplier values that applied to total_win (0 if none or no cluster won).
    multiplier_total: z.number().int().nonnegative().optional(),
    // Free Spins state — 仅在 FS round 进行中或刚刚触发时返回。
    //   remaining: 还剩多少 FS spin（0 = 本次是最后一 spin，session 已结束）
    //   total_win: FS round 累计赢额（含本次）
    //   accumulator: 当前 multiplier accumulator (Sweet Bonanza 风格跨 spin 累积)
    //   ended: 本 spin 后 FS 已结束
    fs_state: z.object({
        remaining: z.number().int().nonnegative(),
        total_win: z.number().int().nonnegative(),
        accumulator: z.number().int().nonnegative(),
        ended: z.boolean(),
    }).nullable().optional(),
    // Room state — 仅当本 spin 在房间内时返回（包含本玩家中奖推 heat bar / 触发 fever 后的最新状态）
    room_state_after: z.unknown().optional(),
    // Lucky Fever 触发 flag —— 本 spin 让 heat 满了，本房刚进入 Lucky Fever 状态
    fever_just_triggered: z.boolean().optional(),
});
//# sourceMappingURL=spin.js.map